import uniq from 'lodash-es/uniq'

// TODO: We should not be using a type from our design system for
// managing size data related to our GraphQL queries...
import { Size } from '@syconium/little-miss-figgy'

import { isDefinedAndNotNull } from '../../app/_utils/isDefinedAndNotNull'
import { Fit } from '../../constants'
import {
  EMBROIDERY_VERSION_V1,
  EMBROIDERY_VERSION_V2,
  EmbroideryVersion,
} from '../../containers/EmbroideryContainer/embroideryVersion'
import { CartItem } from '../../types/figs'

export function deriveMapFromList<Key extends string | number | symbol, Value>(
  list: ReadonlyArray<Value>,
  extractKey: (value: Value) => Key
): Record<Key, Value>
export function deriveMapFromList<Key extends string | number | symbol, Value, T>(
  list: ReadonlyArray<Value>,
  extractKey: (value: Value) => Key,
  transformValue: (value: Value) => T
): Record<Key, T>
export function deriveMapFromList<Key extends string | number | symbol, Value, T>(
  list: ReadonlyArray<Value>,
  extractKey: (value: Value) => Key,
  transformValue?: (value: Value) => T
) {
  return list.reduce(
    (map, current) => ({
      ...map,
      [extractKey(current)]: transformValue ? transformValue(current) : current,
    }),
    {}
  )
}

export function formatPrettyColorName(name: string): string {
  return name.replace(/\s*\/\s*/g, ' / ')
}

/**
 * @deprecated Use ProductCatalog, locally scoped, and generated types. Not hardcoded, shared, fake Zidi based types.
 */
export type DeriveSizesStockState = {
  size: string | null
  soldOut: boolean
}

/**
 * @deprecated Use ProductCatalog types rather than Zidi types.
 */
export function deriveSizes(
  coloredAndFit: DeriveSizesStockState[],
  productGroupSizes: string[]
): Size[] {
  const fullSizesMap = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL']

  const sizeMap =
    productGroupSizes.length >= coloredAndFit.length ? productGroupSizes : fullSizesMap

  const sortSizes = (sizes: Size[]) => {
    const sizeIndexes: { [code: string]: number } = {}
    const sortedSizes: Size[] = []

    for (let i = 0; i < sizes.length; i++) {
      const sizeCode = sizes[i]!.shorthand
      sizeIndexes[sizeCode] = i
    }

    for (let i = 0; i < sizeMap.length; i++) {
      if (sizeIndexes[sizeMap[i]!]! >= 0) {
        sortedSizes.push(sizes[sizeIndexes[sizeMap[i]!]!]!)
      }
    }

    return sortedSizes
  }

  const generatedSizes = coloredAndFit
    .filter(variant => !!variant.size)
    .map(variant => ({
      shorthand: variant.size!,
      id: variant.size!,
      soldOut: variant.soldOut,
    }))

  return sortSizes(generatedSizes)
}

/**
 * @deprecated Use ProductCatalog types rather than Zidi types.
 */
export function isRegularFit(fitKey: string | undefined | null): boolean {
  if (fitKey === undefined || fitKey === null) return false
  return ['', Fit.REGULAR].includes(fitKey)
}

/**
 * @deprecated Use ProductCatalog types rather than Zidi types.
 */
export function areFitsEquivalent(
  fitKey1: string | undefined | null,
  fitKey2: string | undefined | null
): boolean {
  return fitKey1 === fitKey2 || (isRegularFit(fitKey1) && isRegularFit(fitKey2))
}

/**
 * @deprecated Use ProductCatalog types rather than Zidi types.
 */
export function deriveFits(
  currentColorKey: string,
  variants: Array<{
    rawColor: string | null | undefined
    rawFit: string | null | undefined
  }> = [],
  productGroupFitKeys: string[] = []
): string[] {
  const variantFitKeys = uniq(
    variants.filter(variant => variant.rawColor === currentColorKey).map(variant => variant.rawFit)
  ).filter(isDefinedAndNotNull)

  return productGroupFitKeys.filter(fitKey =>
    variantFitKeys.some(variantFitKey => areFitsEquivalent(variantFitKey, fitKey))
  )
}

/**
 * @deprecated Use ProductCatalog types rather than Zidi types.
 */
export function deriveFitNamesMap(productGroup: {
  rawFits: string[] | null
  fits: string[]
}): ReadonlyMap<string, string> {
  const fitNames = new Map<string, string>()
  productGroup.rawFits?.forEach((fitKey, index) => {
    fitNames.set(fitKey, productGroup.fits[index] || fitKey)
  })

  return fitNames
}

const knownEmbroideryVersions: ReadonlySet<string> = new Set<EmbroideryVersion>([
  EMBROIDERY_VERSION_V1,
  EMBROIDERY_VERSION_V2,
])

export function isKnownEmbroideryVersion(
  version: string | null | undefined
): version is EmbroideryVersion {
  return version != null && knownEmbroideryVersions.has(version)
}

export function isEmbroideryItem(cartItem: CartItem): boolean {
  const isEmbroidery = checkIsEmbroideryProduct(cartItem.productType)
  return (
    isEmbroidery &&
    cartItem.properties._guid !== undefined &&
    isKnownEmbroideryVersion(cartItem.properties._embroidery_version)
  )
}

export function isEmbroiderableProduct(cartItem: CartItem): boolean {
  const isEmbroidery = checkIsEmbroideryProduct(cartItem.productType)
  return !isEmbroidery && cartItem.properties._guid !== undefined
}

export type EmbroideryLineKind = 'logoSetup' | 'embroidery' | 'garment' | 'other'

/**
 * Classifies an embroidery-related line. Product type wins when present; otherwise falls back
 * to the property shapes from generateEmbroidery(V2)LineItemProperties.
 */
export function classifyEmbroideryLine(
  properties: Record<string, string | undefined>,
  productType?: string
): EmbroideryLineKind {
  if (properties._is_logo_setup === 'true') return 'logoSetup'
  if (properties._guid === undefined) return 'other'

  const hasKnownVersion = isKnownEmbroideryVersion(properties._embroidery_version)
  if (productType) {
    if (!checkIsEmbroideryProduct(productType)) return 'garment'
    return hasKnownVersion ? 'embroidery' : 'other'
  }

  // v2 garments carry `_embroidery_pricing`; v1.5 garments carry no version.
  if (properties._embroidery_pricing !== undefined) return 'garment'
  if (properties._embroidery_version === undefined) return 'garment'
  return hasKnownVersion ? 'embroidery' : 'other'
}

/**
 * Whether a cart line is eligible for the cart embroidery upsell - the product
 * supports embroidery (embroiderable) and does not already have embroidery attached.
 */
export function isItemEmbroiderableAndUnembroidered(cartItem: CartItem): boolean {
  return (
    cartItem.embroiderable && cartItem.properties._guid === undefined && !isEmbroideryItem(cartItem)
  )
}

export function hasLogoEmbroiderySetup(cartItem: CartItem): boolean {
  return cartItem.properties._logo_setup_fee ? true : false
}

export function addOrRemoveItemFromList<T extends string | number>({
  item,
  list,
  operation,
}: {
  item: T
  list: ReadonlyArray<T>
  operation: 'ADD' | 'REMOVE'
}): T[] {
  if (operation === 'REMOVE' && list.includes(item)) {
    return list.filter(element => element !== item)
  } else if (operation === 'ADD' && !list.includes(item)) {
    return [...list, item]
  } else {
    return [...list]
  }
}

export function toggleItemInList<T extends string | number>(item: T, list: ReadonlyArray<T>): T[] {
  if (list.includes(item)) return list.filter(element => element !== item)
  return [...list, item]
}

export function isRecordEmpty<K extends string | number | symbol, V>(
  record: Record<K, V>
): boolean {
  return Object.keys(record).length === 0
}

export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

export function isServer(): boolean {
  return !isBrowser()
}

export const getQueryParamArray: (
  value: Array<string> | string | null | undefined
) => Array<string> = value => {
  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return Array.isArray(value) ? value : (value as string)?.split(',').filter(Boolean) || []
}

const checkIsEmbroideryProduct = (productType: string) => {
  return productType.toLowerCase() === 'embroidery'
}
